import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import type RFB from '@novnc/novnc';
import {ErrorNotice, Loading, PageHeading} from './components';
import {errorDetails, request} from './lib/store';
import type {ErrorDetails} from './lib/types';
import type {NativeTerminalTicket} from '../src/native-hermes-types';
import './desktop.css';
import {platform, socketError, type PlatformSocket} from './platform/transport';
import {useComposerSize} from './use-composer-size';

function ClipboardEditor({value, onChange, onSend}: {value: string; onChange: (value: string) => void; onSend: () => void}) {
  const textarea = useRef<HTMLTextAreaElement>(null);
  const resize = useComposerSize(textarea, 'measured');
  useLayoutEffect(resize, [resize, value]);

  return <details className="desktop-clipboard" onToggle={resize}><summary>Clipboard</summary>
    <label>Text to copy or paste<textarea ref={textarea} value={value} onChange={event => onChange(event.target.value)}/></label>
    <button className="text-button" onClick={onSend}>Send to desktop</button>
  </details>;
}

export function DesktopPage({csrf}: {csrf: string}) {
  const screen = useRef<HTMLDivElement>(null);
  const connection = useRef<RFB | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [phase, setPhase] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [error, setError] = useState<ErrorDetails | null>(null);
  const [stopping, setStopping] = useState(false);
  const [clipboard, setClipboard] = useState('');

  useEffect(() => {
    const abort = new AbortController();
    let socket: PlatformSocket | undefined;
    let display: RFB | undefined;
    setPhase('connecting');
    setError(null);
    const fail = (failure: unknown) => {
      if (abort.signal.aborted) return;
      setError(errorDetails(failure));
      setPhase('disconnected');
      socket?.close();
    };
    void Promise.all([request<NativeTerminalTicket>('/hermes/desktop', {body: {}, csrf, signal: abort.signal}), import('@novnc/novnc')]).then(([ticket, {default: RFB}]) => {
      if (abort.signal.aborted) return;
      socket = platform().webSocket(ticket.webSocketUrl);
      socket.binaryType = 'arraybuffer';
      socket.addEventListener('open', () => {if (!abort.signal.aborted) socket!.send(JSON.stringify({ticket: ticket.ticket}));});
      socket.addEventListener('error', event => fail(socketError(event, 'desktop')));
      socket.addEventListener('close', event => {
        if (abort.signal.aborted) return;
        setPhase('disconnected');
        if (event.code !== 1000 && event.code !== 1001) setError(current => current ?? errorDetails(new Error(`Desktop connection closed (${event.code}): ${event.reason}`)));
      });
      socket.addEventListener('message', event => {
        if (abort.signal.aborted) return;
        if (typeof event.data !== 'string') return;
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'error') {fail(message.error); return;}
          if (message.type !== 'ready' || display || !screen.current) throw new Error('Unexpected desktop connection response.');
          display = new RFB(screen.current, socket!, {shared: true});
          connection.current = display;
          display.resizeSession = true;
          display.scaleViewport = false;
          display.clipViewport = false;
          display.addEventListener('connect', () => {if (!abort.signal.aborted) setPhase('connected');});
          display.addEventListener('disconnect', event => {
            if (abort.signal.aborted) return;
            if (connection.current === display) connection.current = null;
            setPhase('disconnected');
            if (!(event as CustomEvent<{clean: boolean}>).detail.clean) setError(current => current ?? errorDetails(
              new Error('The desktop session ended with an error. The desktop viewer did not supply further details.')));
          });
          display.addEventListener('securityfailure', event => {
            const details = (event as CustomEvent<{status: number; reason?: string}>).detail;
            fail({name: 'DesktopSecurityError', message: details.reason ?? `Desktop security failed (${details.status}).`, cause: details});
          });
          display.addEventListener('clipboard', event => {
            if (!abort.signal.aborted) setClipboard((event as CustomEvent<{text: string}>).detail.text);
          });
        } catch (failure) {fail(failure);}
      });
    }).catch(failure => {if (!abort.signal.aborted) fail(failure);});
    return () => {abort.abort(); display?.disconnect(); socket?.close(); connection.current = null;};
  }, [csrf, attempt]);

  async function stop() {
    const target = connection.current;
    setStopping(true);
    try {
      await request('/hermes/desktop/stop', {body: {}, csrf});
      target?.disconnect();
      if (connection.current === target) {connection.current = null; setPhase('disconnected');}
    } catch (failure) {setError(errorDetails(failure));}
    finally {setStopping(false);}
  }

  return <section className="desktop-page">
    <Loading active={phase === 'connecting' || stopping}/>
    <PageHeading title="Desktop">Open files and use apps with your mouse and keyboard.</PageHeading>
    <ErrorNotice error={error}/>
    <div className="button-row">
      {phase === 'disconnected' && <button className="button" disabled={stopping} onClick={() => setAttempt(value => value + 1)}>Connect</button>}
      {phase === 'connected' && <button className="text-button" disabled={stopping} onClick={() => void stop()}>Stop desktop</button>}
    </div>
    <div ref={screen} className="desktop-screen" aria-label="Your computer desktop"/>
    {phase === 'connected' && <ClipboardEditor value={clipboard} onChange={setClipboard}
      onSend={() => {connection.current?.clipboardPasteFrom(clipboard); connection.current?.focus();}}/>}
  </section>;
}
